import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import {
  FailedTransactionDetail,
  FailedTransactionFilters,
  PaginatedFailedTransactions
} from '../../types/admin.types';
import TransactionDetailModal from './TransactionDetailModal';
import './FailedTransactionsTable.css';

interface Props {
  onTransactionReprocessed?: () => void;
}

const FailedTransactionsTable: React.FC<Props> = ({ onTransactionReprocessed }) => {
  const [transactions, setTransactions] = useState<PaginatedFailedTransactions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<FailedTransactionDetail | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Filtros
  const [filters, setFilters] = useState<FailedTransactionFilters>({
    PageNumber: 1,
    PageSize: 20,
    Status: 'failed'
  });

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getFailedTransactions(filters);
      setTransactions(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar transacciones');
      console.error('Error loading transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (transaction: FailedTransactionDetail) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const handleReprocessSuccess = () => {
    setShowModal(false);
    setSelectedTransaction(null);
    loadTransactions();
    if (onTransactionReprocessed) {
      onTransactionReprocessed();
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, PageNumber: newPage }));
  };

  const handleFilterChange = (key: keyof FailedTransactionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, PageNumber: 1 }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="failed-transactions-container">
      <div className="section-header">
        <h2>⚠️ Transacciones Fallidas - Gestión Manual</h2>
        <p className="section-description">
          Transacciones que requieren intervención manual. Puedes ver el detalle completo,
          el JSON del webhook original y reprocesarlas una vez solucionado el problema en SAP.
        </p>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Estado:</label>
          <select
            value={filters.Status || ''}
            onChange={(e) => handleFilterChange('Status', e.target.value || undefined)}
          >
            <option value="">Todos</option>
            <option value="failed">Fallidas</option>
            <option value="pending">Pendientes</option>
            <option value="processing">Procesando</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Placa:</label>
          <input
            type="text"
            placeholder="Buscar por placa..."
            value={filters.Placa || ''}
            onChange={(e) => handleFilterChange('Placa', e.target.value || undefined)}
          />
        </div>

        <div className="filter-group">
          <label>Identificación:</label>
          <input
            type="text"
            placeholder="Buscar por identificación..."
            value={filters.CustomerIdentification || ''}
            onChange={(e) => handleFilterChange('CustomerIdentification', e.target.value || undefined)}
          />
        </div>

        <button className="btn-refresh" onClick={loadTransactions} disabled={loading}>
          {loading ? '🔄 Cargando...' : '🔄 Actualizar'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Loading State */}
      {loading && !transactions && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando transacciones...</p>
        </div>
      )}

      {/* Tabla de Transacciones */}
      {transactions && (
        <>
          <div className="table-container">
            <table className="failed-transactions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Placa</th>
                  <th>Cliente</th>
                  <th>Monto</th>
                  <th>Estado</th>
                  <th>Error</th>
                  <th>Reintentos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transactions.Transactions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty-message">
                      ✅ No hay transacciones fallidas en este momento
                    </td>
                  </tr>
                ) : (
                  transactions.Transactions.map((tx) => (
                    <tr key={tx.TransactionId}>
                      <td className="id-column">{tx.TransactionId}</td>
                      <td className="date-column">{formatDate(tx.CreatedAt)}</td>
                      <td className="placa-column">
                        <strong>{tx.Placa}</strong>
                      </td>
                      <td className="customer-column">
                        <div className="customer-info">
                          <div className="customer-name">{tx.CustomerName || 'Sin nombre'}</div>
                          <div className="customer-id">{tx.CustomerIdentification}</div>
                          {tx.CustomerEmail && (
                            <div className="customer-email">{tx.CustomerEmail}</div>
                          )}
                        </div>
                      </td>
                      <td className="amount-column">{formatCurrency(tx.Amount)}</td>
                      <td>
                        <span className={`status-badge status-${tx.Status}`}>
                          {tx.Status}
                        </span>
                      </td>
                      <td className="error-column">
                        {tx.ErrorMessage ? (
                          <div className="error-preview" title={tx.ErrorMessage}>
                            {tx.ErrorMessage.substring(0, 50)}
                            {tx.ErrorMessage.length > 50 && '...'}
                          </div>
                        ) : (
                          <span className="no-error">—</span>
                        )}
                      </td>
                      <td className="retry-column">
                        <span className={`retry-badge ${tx.RetryCount > 0 ? 'has-retries' : ''}`}>
                          {tx.RetryCount}
                        </span>
                      </td>
                      <td className="actions-column">
                        <button
                          className="btn-view-detail"
                          onClick={() => handleViewDetail(tx)}
                          title="Ver detalle completo y reprocesar"
                        >
                          🔍 Ver Detalle
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {transactions.TotalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-pagination"
                onClick={() => handlePageChange(transactions.PageNumber - 1)}
                disabled={!transactions.HasPreviousPage}
              >
                ← Anterior
              </button>

              <span className="pagination-info">
                Página {transactions.PageNumber} de {transactions.TotalPages}
                {' '}({transactions.TotalCount} transacciones)
              </span>

              <button
                className="btn-pagination"
                onClick={() => handlePageChange(transactions.PageNumber + 1)}
                disabled={!transactions.HasNextPage}
              >
                Siguiente →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de Detalle */}
      {showModal && selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setShowModal(false)}
          onReprocessSuccess={handleReprocessSuccess}
        />
      )}
    </div>
  );
};

export default FailedTransactionsTable;
